/**
 * Payment Settings Page
 *
 * State-driven Stripe Connect onboarding for guides. Renders one of 5 states
 * based on the guide's onboarding progress, with an inline progress bar.
 *
 * Epic: MVP 1.21.6 - Guide Revenue
 * Story: SCRUM-1268 - PaymentSettings state-driven UI + progress bar (5 SP)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiRequest } from '../../services/api';
import {
  CreditCard,
  CheckCircle,
  AlertCircle,
  Loader2,
  ExternalLink,
  ArrowLeft,
  XCircle
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';

const REQUIREMENT_LABELS = {
  'individual.id_number': 'Government ID verification',
  'individual.verification.document': 'Identity document upload',
  'individual.dob.day': 'Date of birth',
  'individual.dob.month': 'Date of birth',
  'individual.dob.year': 'Date of birth',
  'individual.address.line1': 'Home address',
  'individual.address.city': 'Home address',
  'individual.address.state': 'Home address',
  'individual.address.postal_code': 'Home address',
  'external_account': 'Bank account details',
  'tos_acceptance.date': 'Terms of service acceptance',
  'tos_acceptance.ip': 'Terms of service acceptance',
};

function formatRequirement(req) {
  return REQUIREMENT_LABELS[req] || 'Additional verification required';
}

export default function PaymentSettings() {
  const navigate = useNavigate();
  const handleNavigate = (id, path) => { if (path) { navigate(path); } };
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);

  const returnedFromStripe = searchParams.get('stripe_return') === 'true';

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiRequest('/api/v1/guide/stripe/status');
      setStatus(response.data);
    } catch (err) {
      // 404 means guide has no Stripe account yet — State 1 (not started)
      if (err.status === 404 || (err.message && err.message.includes('no Stripe account'))) {
        setStatus({
          stripeAccountId: null,
          onboardingComplete: false,
          chargesEnabled: false,
          payoutsEnabled: false,
          currentlyDue: [],
          disabledReason: null,
        });
      } else {
        setError(err.message || 'Failed to load payment status');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError(null);

      const currentUrl = window.location.origin;
      const response = await apiRequest('/api/v1/guide/stripe/onboarding', {
        method: 'POST',
        body: JSON.stringify({
          refreshUrl: `${currentUrl}/guide/dashboard/settings/payments`,
          returnUrl: `${currentUrl}/guide/dashboard/settings/payments?stripe_return=true`
        })
      });

      if (response.data && response.data.accountLinkUrl) {
        window.location.href = response.data.accountLinkUrl;
      }
    } catch (err) {
      setError(err.message || 'Failed to start Stripe Connect setup');
      setConnecting(false);
    }
  };

  // Derived states
  const isComplete = status && status.chargesEnabled && status.payoutsEnabled;
  const isInProgress = status && status.stripeAccountId && !isComplete;
  const isProcessing = status && status.onboardingComplete && !isComplete && !status.disabledReason;
  const isRejected = status && status.disabledReason && status.disabledReason.includes('rejected');
  const isRestricted = status && !isComplete && !isProcessing && !isRejected &&
    ((status.currentlyDue && status.currentlyDue.length > 0) ||
     (status.disabledReason && !status.disabledReason.includes('rejected')));

  // Auto-refresh for processing state
  useEffect(() => {
    if (isProcessing) {
      const timer = setInterval(() => {
        fetchStatus();
      }, 30000);
      return () => clearInterval(timer);
    }
  }, [isProcessing]);

  const getProgressConfig = () => {
    if (isRejected) { return null; }
    if (isComplete) {
      return {
        stepLabel: 'Step 3 of 3 — Payouts Active',
        rightLabel: '✓ Complete',
        percent: 100,
        rightColor: 'var(--color-success)',
        fillColor: 'var(--color-success)',
      };
    }
    if (isRestricted) {
      return {
        stepLabel: 'Step 2 of 3 — Action Required',
        rightLabel: 'Almost there',
        percent: 66,
        rightColor: 'var(--color-warning-dark)',
        fillColor: 'var(--color-warning)',
      };
    }
    if (isProcessing || isInProgress) {
      return {
        stepLabel: 'Step 2 of 3 — Complete Verification',
        rightLabel: 'Almost there',
        percent: 66,
        rightColor: 'var(--color-text-secondary)',
        fillColor: 'var(--color-primary)',
      };
    }
    return {
      stepLabel: 'Step 1 of 3 — Create Account',
      rightLabel: '~5 minutes to complete',
      percent: 33,
      rightColor: 'var(--color-text-secondary)',
      fillColor: 'var(--color-primary)',
    };
  };

  const getDeduplicatedRequirements = () => {
    if (!status || !status.currentlyDue) { return []; }
    const labels = status.currentlyDue.map(formatRequirement);
    return [...new Set(labels)];
  };

  const primaryButtonStyle = {
    width: '100%',
    padding: 'var(--space-3) var(--space-4)',
    backgroundColor: connecting ? 'var(--color-gray-300)' : 'var(--color-primary)',
    color: 'var(--color-white)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-md)',
    fontWeight: 'var(--font-weight-semibold)',
    cursor: connecting ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--space-2)',
    transition: 'all var(--transition-fast)',
  };

  return (
    <DashboardLayout userRole="guide" activeNavId="payment-settings" onNavigate={handleNavigate}>
      <div style={{ maxWidth: '640px', margin: '0 auto', padding: 'var(--space-6)' }}>
        {/* Back navigation */}
        <button
          onClick={() => navigate('/guide/dashboard')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            padding: 0,
            marginBottom: 'var(--space-6)'
          }}
        >
          <ArrowLeft size={16} />
          Back to Dashboard
        </button>

        {/* Header */}
        <div style={{ marginBottom: 'var(--space-6)' }}>
          <h1 style={{
            fontSize: 'var(--font-size-2xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-text-primary)',
            margin: 0,
            marginBottom: 'var(--space-2)'
          }}>
            Payment Settings
          </h1>
          <p style={{
            fontSize: 'var(--font-size-md)',
            color: 'var(--color-text-secondary)',
            margin: 0
          }}>
            Connect your bank account through Stripe to receive payouts from bookings.
          </p>
        </div>

        {loading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            padding: 'var(--space-8)'
          }}>
            <Loader2 size={32} style={{
              animation: 'spin 1s linear infinite',
              color: 'var(--color-primary)'
            }} />
          </div>
        ) : (
          <div style={{
            backgroundColor: 'var(--color-white)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--color-border)',
            padding: 'var(--space-6)',
            boxShadow: 'var(--shadow-sm)'
          }}>
            {/* Return from Stripe banners */}
            {returnedFromStripe && isComplete && (
              <div style={{
                backgroundColor: 'var(--color-success-light)',
                border: '1px solid var(--color-success)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-4)',
                marginBottom: 'var(--space-4)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-3)'
              }}>
                <CheckCircle size={20} style={{ color: 'var(--color-success)', flexShrink: 0, marginTop: '2px' }} />
                <p style={{ margin: 0, fontWeight: 'var(--font-weight-medium)', color: 'var(--color-success)' }}>
                  Setup complete! You&apos;re ready to receive payouts.
                </p>
              </div>
            )}

            {returnedFromStripe && !isComplete && (
              <div style={{
                backgroundColor: 'var(--color-warning-light)',
                border: '1px solid var(--color-warning)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-4)',
                marginBottom: 'var(--space-4)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-3)'
              }}>
                <AlertCircle size={20} style={{ color: 'var(--color-warning-dark)', flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <p style={{ margin: 0, fontWeight: 'var(--font-weight-medium)', color: 'var(--color-warning-dark)' }}>
                    Still processing
                  </p>
                  <p style={{ margin: 0, marginTop: 'var(--space-1)', fontSize: 'var(--font-size-sm)', color: 'var(--color-warning-dark)' }}>
                    Stripe may take a few minutes to verify your information. This page will refresh automatically.
                  </p>
                </div>
              </div>
            )}

            {/* Progress Bar */}
            {status && (() => {
              const config = getProgressConfig();
              if (!config) { return null; }
              return (
                <div data-testid="progress-bar" style={{ marginBottom: 'var(--space-6)' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--space-2)'
                  }}>
                    <span data-testid="step-label">{config.stepLabel}</span>
                    <span style={{ color: config.rightColor }}>{config.rightLabel}</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: 'var(--color-gray-200)',
                    borderRadius: '9999px',
                    overflow: 'hidden'
                  }}>
                    <div
                      data-testid="progress-fill"
                      style={{
                        height: '100%',
                        backgroundColor: config.fillColor,
                        borderRadius: '9999px',
                        transition: 'width 0.3s ease',
                        width: `${config.percent}%`
                      }}
                    />
                  </div>
                </div>
              );
            })()}

            {/* Error display */}
            {error && (
              <div style={{
                backgroundColor: 'var(--color-error-light)',
                border: '1px solid var(--color-error)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-3)',
                marginBottom: 'var(--space-4)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)'
              }}>
                <AlertCircle size={16} style={{ color: 'var(--color-error)', flexShrink: 0 }} />
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-error)' }}>{error}</span>
              </div>
            )}

            {/* State 1 — Not Started */}
            {status && !status.stripeAccountId && !isRejected && !isRestricted && (
              <div style={{ textAlign: 'center' }}>
                <CreditCard size={48} style={{ color: 'var(--color-primary)', margin: '0 auto', marginBottom: 'var(--space-4)' }} />
                <h2 style={{
                  fontSize: 'var(--font-size-xl)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--color-text-primary)',
                  margin: 0,
                  marginBottom: 'var(--space-2)'
                }}>
                  Get Paid for Your Bookings
                </h2>
                <p style={{
                  color: 'var(--color-text-secondary)',
                  margin: 0,
                  marginBottom: 'var(--space-6)'
                }}>
                  Set up your payout account to receive payments directly to your bank.
                  Takes about 5 minutes through Stripe&apos;s secure setup.
                </p>
                <button
                  onClick={handleConnect}
                  disabled={connecting}
                  data-testid="setup-button"
                  style={primaryButtonStyle}
                >
                  {connecting ? (
                    <>
                      <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <ExternalLink size={18} />
                      Set Up Payouts
                    </>
                  )}
                </button>
              </div>
            )}

            {/* State 2 — In Progress (started, not submitted) */}
            {status && status.stripeAccountId && !status.onboardingComplete && !isRestricted && !isRejected && (
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-4)',
                  marginBottom: 'var(--space-4)'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: 'var(--radius-full)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'var(--color-warning-light)',
                    flexShrink: 0
                  }}>
                    <CreditCard size={24} style={{ color: 'var(--color-warning-dark)' }} />
                  </div>
                  <div>
                    <h3 style={{
                      fontSize: 'var(--font-size-lg)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--color-text-primary)',
                      margin: 0
                    }}>
                      Setup In Progress
                    </h3>
                    <p style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-text-secondary)',
                      margin: 0,
                      marginTop: 'var(--space-1)'
                    }}>
                      You&apos;ve started the Stripe onboarding process. Complete the remaining steps to start receiving payouts.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleConnect}
                  disabled={connecting}
                  data-testid="continue-button"
                  style={primaryButtonStyle}
                >
                  {connecting ? (
                    <>
                      <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <ExternalLink size={18} />
                      Continue Setup
                    </>
                  )}
                </button>
              </div>
            )}

            {/* State 2b — Processing (submitted, not yet enabled) */}
            {isProcessing && (
              <div style={{ textAlign: 'center' }}>
                <Loader2 size={48} style={{
                  animation: 'spin 1s linear infinite',
                  color: 'var(--color-primary)',
                  margin: '0 auto',
                  marginBottom: 'var(--space-4)'
                }} />
                <h3 style={{
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--color-text-primary)',
                  margin: 0,
                  marginBottom: 'var(--space-2)'
                }}>
                  Stripe Is Verifying Your Account
                </h3>
                <p style={{
                  color: 'var(--color-text-secondary)',
                  margin: 0,
                  marginBottom: 'var(--space-4)'
                }}>
                  Your information has been submitted and Stripe is reviewing your account.
                  This usually takes a few minutes.
                </p>
                <p data-testid="auto-refresh-note" style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-secondary)',
                  margin: 0
                }}>
                  This page refreshes automatically every 30 seconds.
                </p>
              </div>
            )}

            {/* State 3 — Complete */}
            {isComplete && (
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-4)',
                  marginBottom: 'var(--space-4)'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: 'var(--radius-full)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'var(--color-success-light)',
                    flexShrink: 0
                  }}>
                    <CheckCircle size={24} style={{ color: 'var(--color-success)' }} />
                  </div>
                  <div>
                    <h3 style={{
                      fontSize: 'var(--font-size-lg)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--color-text-primary)',
                      margin: 0
                    }}>
                      Payouts Active
                    </h3>
                    <p style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-text-secondary)',
                      margin: 0,
                      marginTop: 'var(--space-1)'
                    }}>
                      Your account is fully set up to accept payments and receive payouts.
                    </p>
                  </div>
                </div>

                {/* Payout schedule info */}
                <div style={{
                  backgroundColor: 'var(--color-gray-50)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-4)',
                  marginBottom: 'var(--space-4)'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--font-size-sm)' }}>
                      <span style={{ color: 'var(--color-text-secondary)' }}>Charges</span>
                      <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--color-success)' }}>Enabled</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--font-size-sm)' }}>
                      <span style={{ color: 'var(--color-text-secondary)' }}>Payouts</span>
                      <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--color-success)' }}>Enabled</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--font-size-sm)' }}>
                      <span style={{ color: 'var(--color-text-secondary)' }}>Payout Schedule</span>
                      <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-primary)' }}>2 days after trip completion</span>
                    </div>
                  </div>
                </div>

                {/* Update bank account link */}
                <button
                  onClick={handleConnect}
                  disabled={connecting}
                  data-testid="update-button"
                  style={{
                    width: '100%',
                    padding: 'var(--space-3) var(--space-4)',
                    backgroundColor: 'transparent',
                    color: 'var(--color-primary)',
                    border: '1px solid var(--color-primary)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-md)',
                    fontWeight: 'var(--font-weight-semibold)',
                    cursor: connecting ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--space-2)',
                    transition: 'all var(--transition-fast)',
                  }}
                >
                  <ExternalLink size={18} />
                  Update Bank Account
                </button>
              </div>
            )}

            {/* State 4 — Restricted */}
            {isRestricted && (
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-4)',
                  marginBottom: 'var(--space-4)'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: 'var(--radius-full)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'var(--color-warning-light)',
                    flexShrink: 0
                  }}>
                    <AlertCircle size={24} style={{ color: 'var(--color-warning-dark)' }} />
                  </div>
                  <div>
                    <h3 style={{
                      fontSize: 'var(--font-size-lg)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--color-text-primary)',
                      margin: 0
                    }}>
                      Action Required
                    </h3>
                    <p style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-text-secondary)',
                      margin: 0,
                      marginTop: 'var(--space-1)'
                    }}>
                      Stripe needs additional information to enable your payouts.
                    </p>
                  </div>
                </div>

                {/* Requirements list */}
                <div style={{
                  backgroundColor: 'var(--color-warning-light)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-4)',
                  marginBottom: 'var(--space-4)'
                }}>
                  <p style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--color-warning-dark)',
                    margin: 0,
                    marginBottom: 'var(--space-2)'
                  }}>
                    Items to resolve:
                  </p>
                  <ul data-testid="requirements-list" style={{
                    margin: 0,
                    paddingLeft: 'var(--space-4)',
                    listStyleType: 'disc'
                  }}>
                    {getDeduplicatedRequirements().map((label) => (
                      <li key={label} style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-warning-dark)',
                        marginBottom: 'var(--space-1)'
                      }}>
                        {label}
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={handleConnect}
                  disabled={connecting}
                  data-testid="resolve-button"
                  style={primaryButtonStyle}
                >
                  {connecting ? (
                    <>
                      <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <ExternalLink size={18} />
                      Resolve Issues
                    </>
                  )}
                </button>
              </div>
            )}

            {/* State 5 — Rejected */}
            {isRejected && (
              <div style={{ textAlign: 'center' }}>
                <XCircle size={48} style={{ color: 'var(--color-error)', margin: '0 auto', marginBottom: 'var(--space-4)' }} />
                <h2 style={{
                  fontSize: 'var(--font-size-xl)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--color-text-primary)',
                  margin: 0,
                  marginBottom: 'var(--space-2)'
                }}>
                  Account Not Approved
                </h2>
                <p style={{
                  color: 'var(--color-text-secondary)',
                  margin: 0,
                  marginBottom: 'var(--space-6)'
                }}>
                  Unfortunately, Stripe was unable to approve your account. Please contact our support team for assistance.
                </p>
                <a
                  href="mailto:support@outdoorguides.com?subject=Stripe%20Account%20Not%20Approved"
                  data-testid="contact-support"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-3) var(--space-4)',
                    backgroundColor: 'var(--color-primary)',
                    color: 'var(--color-white)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-md)',
                    fontWeight: 'var(--font-weight-semibold)',
                    textDecoration: 'none',
                    cursor: 'pointer',
                  }}
                >
                  Contact Support
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
